<template>
  <b-modal
    v-model="showModal"
    size="lg"
    id="manage-shipping-modal"
    :title="isEdit ? `Edit shipping #${edited.id}` : `Create shipping for order #${sale?.id}`"
    :ok-title="isFormLoading ? 'Saving...' : 'Save'"
    :ok-disabled="isFormLoading || isContentLoading"
    cancel-variant="outline-secondary"
    @ok="onSubmit($event)"
  >
    <div
      v-if="isContentLoading"
      class="text-center pt-3"
    >
      <b-spinner label="Loading..." />
    </div>
    <b-form v-else>
      <b-row>
        <b-col cols="12">
          <b-form-group label="Carrier name" label-for="shipping-carrier-name">
            <b-form-input
              id="shipping-carrier-name"
              v-model="edited.carrier_name"
              placeholder="Carrier name"
            />
            <small v-if="v$.edited?.carrier_name.$error" class="text-danger">Carrier name is required.</small>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
          <b-form-group label="Shipping date" label-for="shipping-date">
            <b-form-datepicker
              id="shipping-date"
              v-model="edited.shipping_date"
              reset-button
            />
            <small v-if="v$.edited?.shipping_date.$error" class="text-danger">Shipping date is required.</small>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
          <b-form-group label="Tracking ID" label-for="shipping-tracking-id">
            <b-form-input
              id="shipping-tracking-id"
              v-model="edited.tracking_id"
              placeholder="Tracking ID"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
          <b-form-group label="Tracking URL" label-for="shipping-tracking-url">
            <b-form-input
              id="shipping-tracking-url"
              v-model="edited.tracking_url"
              placeholder="https://..."
            />
          </b-form-group>
        </b-col>
      </b-row>

      <h6 class="mt-1 mb-1">Units to ship</h6>
      <div
        v-for="unit in unitRows"
        :key="unit.unit_id"
        class="d-flex align-items-center mb-1 border rounded p-1"
      >
        <b-form-checkbox
          v-model="unit.selected"
          class="mr-1"
          @change="onUnitSelectionChange(unit)"
        />
        <div class="flex-grow-1 mr-1">
          <div class="font-weight-bold">{{ unit.name }}</div>
          <small class="text-muted">Remaining: {{ unit.max_quantity }}</small>
        </div>
        <b-form-input
          type="number"
          min="1"
          :max="unit.max_quantity"
          step="1"
          class="unit-qty-input"
          v-model.number="unit.quantity"
          :disabled="!unit.selected || unit.max_quantity <= 0"
          style="width: 90px;"
        />
      </div>
      <small v-if="unitsError" class="text-danger">{{ unitsError }}</small>
    </b-form>
  </b-modal>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import {
  BCol,
  BForm,
  BFormCheckbox,
  BFormDatepicker,
  BFormGroup,
  BFormInput,
  BModal,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import { toastFailure, toastSuccess } from '@/libs/toastification'
import { backendErrorString, normalizeTrackingUrl } from '@/libs/utils'
import { companyTodayYmd } from '@/libs/companyTime'

const props = defineProps({
  sale: {
    type: Object,
    default: null,
  },
  shippingId: {
    type: Number,
    default: null,
  },
  clickDate: {
    type: Date,
    required: true,
  },
})

const emit = defineEmits(['saved'])

const store = useStore()
const showModal = ref(false)
const isContentLoading = ref(true)
const isFormLoading = ref(false)
const unitsError = ref('')
const unitRows = ref([])

const edited = reactive({
  id: null,
  carrier_name: '',
  tracking_url: '',
  tracking_id: '',
  shipping_date: null,
})

const isEdit = computed(() => !!(props.shippingId && props.shippingId > 0))

const todayDateString = () => companyTodayYmd()

const rules = {
  edited: {
    carrier_name: { required },
    shipping_date: { required },
  },
}
const v$ = useVuelidate(rules, { edited })

const buildUnitRows = (context, shipping = null) => {
  const shippingUnits = {}
  if (shipping && Array.isArray(shipping.units)) {
    shipping.units.forEach((unit) => {
      shippingUnits[unit.id] = unit.quantity
    })
  }

  unitRows.value = (context.units || []).map((unit) => {
    const maxQuantity = isEdit.value
      ? unit.remaining_quantity + (shippingUnits[unit.id] || 0)
      : unit.remaining_quantity
    const isSelected = isEdit.value
      ? Object.prototype.hasOwnProperty.call(shippingUnits, unit.id)
      : maxQuantity > 0

    return {
      unit_id: unit.id,
      name: unit.name,
      max_quantity: maxQuantity,
      selected: isSelected && maxQuantity > 0,
      quantity: isEdit.value && shippingUnits[unit.id]
        ? shippingUnits[unit.id]
        : maxQuantity,
    }
  })
}

const loadModalData = async () => {
  if (!props.sale || !props.sale.id) {
    return
  }

  isContentLoading.value = true
  unitsError.value = ''

  try {
    const excludeShippingId = isEdit.value ? props.shippingId : null
    const context = await store.dispatch('shippings/loadShippingContext', {
      saleId: props.sale.id,
      excludeShippingId,
    })

    if (isEdit.value) {
      const shipping = await store.dispatch('shippings/loadShipping', props.shippingId)
      edited.id = shipping.id
      edited.carrier_name = shipping.carrier_name || ''
      edited.tracking_url = shipping.tracking_url || ''
      edited.tracking_id = shipping.tracking_id || ''
      edited.shipping_date = shipping.shipping_date || todayDateString()
      buildUnitRows(context, shipping)
    } else {
      edited.id = null
      edited.carrier_name = ''
      edited.tracking_url = ''
      edited.tracking_id = ''
      edited.shipping_date = todayDateString()
      buildUnitRows(context)
    }

    showModal.value = true
  } catch (error) {
    showModal.value = false
    toastFailure(backendErrorString(error))
  } finally {
    isContentLoading.value = false
  }
}

watch(
  () => props.clickDate,
  () => {
    if (!props.sale) {
      return
    }
    loadModalData()
  },
)

const onUnitSelectionChange = (unit) => {
  if (unit.selected && (!unit.quantity || unit.quantity < 1)) {
    unit.quantity = unit.max_quantity > 0 ? unit.max_quantity : 1
  }
}

const validateUnits = () => {
  const selectedUnits = unitRows.value.filter((unit) => unit.selected && unit.quantity > 0)
  if (!selectedUnits.length) {
    unitsError.value = 'Select at least one unit with quantity greater than zero.'
    return false
  }

  for (const unit of selectedUnits) {
    if (unit.quantity > unit.max_quantity) {
      unitsError.value = `Quantity for ${unit.name} cannot exceed ${unit.max_quantity}.`
      return false
    }
  }

  unitsError.value = ''
  return true
}

const onSubmit = async (event) => {
  event.preventDefault()
  if (isFormLoading.value) {
    return
  }

  const isFormCorrect = await v$.value.edited.$validate()
  if (!isFormCorrect || !validateUnits()) {
    return
  }

  isFormLoading.value = true

  const payload = {
    carrier_name: edited.carrier_name,
    tracking_url: normalizeTrackingUrl(edited.tracking_url),
    tracking_id: edited.tracking_id || null,
    shipping_date: edited.shipping_date,
    units: unitRows.value
      .filter((unit) => unit.selected && unit.quantity > 0)
      .map((unit) => ({
        unit_id: unit.unit_id,
        quantity: unit.quantity,
        selected: true,
      })),
  }

  try {
    if (isEdit.value) {
      const updated = await store.dispatch('shippings/updateShipping', { id: props.shippingId, payload })
      toastSuccess('Shipping updated successfully.')
      showModal.value = false
      const context = await store.dispatch('shippings/loadShippingContext', {
        saleId: props.sale.id,
      })
      emit('saved', {
        has_remaining_to_ship: !!(context && context.has_remaining_to_ship),
        has_any_shipped: !!(context && context.has_any_shipped),
        sale_status: updated?.sale_status,
      })
      return
    }

    const created = await store.dispatch('shippings/createShipping', { saleId: props.sale.id, payload })
    toastSuccess('Shipping created successfully.')
    showModal.value = false
    const context = await store.dispatch('shippings/loadShippingContext', {
      saleId: props.sale.id,
    })
    emit('saved', {
      has_remaining_to_ship: !!(context && context.has_remaining_to_ship),
      has_any_shipped: !!(context && context.has_any_shipped),
      sale_status: created?.sale_status,
    })
  } catch (error) {
    toastFailure(backendErrorString(error))
  } finally {
    isFormLoading.value = false
  }
}
</script>
